import { isAxiosError } from 'axios';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState
} from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../services/api';

const AuthContext = createContext();

export default function AuthProvider ({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(null);
  const [acceptedAt, setAcceptedAt] = useState(null);
  const navigate = useNavigate();

  const userCan = useCallback(
    (permission, checkApproved) => {
      const permissions = JSON.parse(localStorage.getItem('permissions'))
      if (!permissions) return false
      const user = JSON.parse(localStorage.getItem('user'))
      if (checkApproved && !user?.approved) return false
      return permissions.includes(permission)
    },
    []
  );


  const fetchPermissions = async () => {
    const response = await api.get('/permissions')
    return response.data
  }

  const fetchAccept = async () => {
    const response = await api.get('/accept')
    return response.data
  }

  const saveLogin = useCallback(
    async (response) => {
      const { data } = response

      localStorage.setItem('token', data.token);
      localStorage.setItem('refreshToken', data.refreshToken);
      localStorage.setItem('user', JSON.stringify(data.user));
      localStorage.setItem('species', JSON.stringify(data.species));

      setToken(data.token);
      setRefreshToken(data.refreshToken);
      setUser(data.user);

      if (response.data.user.hasPassword === false) {
        return;
      }

      const permissions = await fetchPermissions()
      localStorage.setItem('permissions', JSON.stringify(permissions))

      const acceptedAt = await fetchAccept()
      localStorage.setItem('acceptedAt', acceptedAt)
      setAcceptedAt(acceptedAt);

      if (response.data.user.hasPassword === false) {
        return;
      }

      if (!acceptedAt) {
        navigate('/cadastre-se');
        return response
      }
      if (data.token) {
        navigate('/bem-vindo');
      }

      return response;
    },
    [navigate]
  );

  const doLogin = useCallback(
    async (username, password) => {
      const responseMessage = {
        'Invalid credentials': 'Credenciais inválidas',
        'Missing param: username': 'Preencha o login',
        'Missing param: password': 'Preencha a senha',
      }

      try {
        const response = await api.post('session', { username, password });
        return saveLogin(response);
      } catch (error) {
        if (!isAxiosError(error)) {
          toast.error('Acesso indisponivel, verifique sua conexão de rede.', {
            position: "bottom-right",
            theme: 'colored'
          });
          return;
        }
        if (responseMessage[error.response.data.message]) {
          toast.error(responseMessage[error.response.data.message], {
            position: "bottom-right",
            theme: 'colored'
          })
          return
        }
        toast.error('Ocorreu um erro inesperado, por favor tente novamente mais tarde', {
          position: "bottom-right",
          theme: 'colored'
        })
      }
    },
    [saveLogin]
  );

  useEffect(() => {
    const tokenData = localStorage.getItem('token');
    if (!tokenData) return;

    const userData = JSON.parse(localStorage.getItem('user'));

    setToken(tokenData);
    setUser(userData);
  }, []);

  const doLogout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('species');
    setToken(null);
    setUser(null);

    window.location = '/';
  }, []);

  useEffect(() => {
    if (!token) return;
    const timeout = setTimeout(async () => {
      try {
        const response = await api.post('/session/refresh', null, {
          headers: {
            Authorization: `Bearer ${refreshToken}`
          }
        });
        const { token: newToken, refreshToken: newRefreshToken } =
          response.data;
        localStorage.setItem('token', newToken);
        localStorage.setItem('refreshToken', newRefreshToken);
        setToken(newToken);
        setRefreshToken(newRefreshToken);
      } catch (error) {
        toast.error('O token expirou', { theme: 'colored' });
        doLogout();
      }
    }, 30 * 60 * 1000);

    return () => clearTimeout(timeout);
  }, [token, refreshToken, doLogout]);

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        acceptedAt,
        setUser,
        doLogin,
        doLogout,
        saveLogin,
        userCan
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth () {
  const context = useContext(AuthContext);

  if (!context) throw new Error('useAuth must be used within a AuthProvider');

  return context;
}